import React, { useState, useEffect } from 'react';
import { FaTrophy, FaPlus, FaEdit, FaTrash, FaEye, FaCheck, FaTimes, FaSync, FaPlay, FaPause, FaGlobe, FaUsers, FaExclamationTriangle } from 'react-icons/fa';
import AdminService from '../services/AdminService';
import toast from 'react-hot-toast';
import './AdminChallengesView.css';

const AdminChallengesView = ({ adminLevel, hasAdminLevel }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    league: '10k',
    startingBalance: 10000,
    maxParticipants: '',
    prizePool: '',
    rules: {}
  });

  useEffect(() => {
    if (hasAdminLevel('2')) {
      loadChallenges();
    }
  }, []);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getChallenges();
      setChallenges(data.challenges || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    try {
      await AdminService.createChallenge({
        ...formData,
        startingBalance: parseFloat(formData.startingBalance),
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        prizePool: formData.prizePool ? parseFloat(formData.prizePool) : null,
      });
      toast.success('Challenge created successfully');
      setShowCreateModal(false);
      resetForm();
      loadChallenges();
    } catch (error) {
      toast.error(error.message || 'Failed to create challenge');
    }
  };

  const handleUpdateChallenge = async (e) => {
    e.preventDefault();
    try {
      await AdminService.updateChallenge(selectedChallenge.id, {
        ...formData,
        startingBalance: parseFloat(formData.startingBalance),
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        prizePool: formData.prizePool ? parseFloat(formData.prizePool) : null,
      });
      toast.success('Challenge updated successfully');
      setShowEditModal(false);
      setSelectedChallenge(null);
      resetForm();
      loadChallenges();
    } catch (error) {
      toast.error(error.message || 'Failed to update challenge');
    }
  };

  const handleAction = async (action, challengeId) => {
    try {
      switch (action) {
        case 'publish':
          await AdminService.publishChallenge(challengeId);
          toast.success('Challenge published');
          break;
        case 'unpublish':
          await AdminService.unpublishChallenge(challengeId);
          toast.success('Challenge unpublished');
          break;
        case 'activate':
          await AdminService.activateChallenge(challengeId);
          toast.success('Challenge activated');
          break;
        case 'deactivate':
          await AdminService.deactivateChallenge(challengeId);
          toast.success('Challenge deactivated');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this challenge? This action cannot be undone.')) {
            await AdminService.deleteChallenge(challengeId);
            toast.success('Challenge deleted');
          }
          break;
        case 'forceDelete':
          const challenge = challenges.find(c => c.id === challengeId);
          const participantCount = challenge?.participantCount || 0;
          const confirmMessage = participantCount > 0
            ? `⚠️ WARNING: Force Delete Challenge\n\nThis challenge has ${participantCount} participant(s). Force deleting will permanently remove the challenge and ALL participant data.\n\nThis action is IRREVERSIBLE and should only be used when exploiting is detected.\n\nType "FORCE DELETE" to confirm:`
            : 'Are you sure you want to force delete this challenge? This action cannot be undone.';
          
          if (participantCount > 0) {
            const userInput = window.prompt(confirmMessage);
            if (userInput !== 'FORCE DELETE') {
              toast.error('Force delete cancelled. You must type "FORCE DELETE" to confirm.');
              return;
            }
          } else {
            if (!window.confirm(confirmMessage)) {
              return;
            }
          }
          
          await AdminService.forceDeleteChallenge(challengeId);
          toast.success(`Challenge force deleted${participantCount > 0 ? ` (${participantCount} participants removed)` : ''}`);
          break;
        case 'sync':
          await AdminService.syncChallengeParticipants(challengeId);
          toast.success('Participant values synced');
          break;
        default:
          return;
      }
      loadChallenges();
    } catch (error) {
      toast.error(error.message || 'Failed to perform action');
    }
  };

  const openEditModal = (challenge) => {
    setSelectedChallenge(challenge);
    setFormData({
      title: challenge.title || '',
      description: challenge.description || '',
      startDate: challenge.startDate ? new Date(challenge.startDate).toISOString().slice(0, 16) : '',
      endDate: challenge.endDate ? new Date(challenge.endDate).toISOString().slice(0, 16) : '',
      league: challenge.league || '10k',
      startingBalance: challenge.startingBalance || 10000,
      maxParticipants: challenge.maxParticipants || '',
      prizePool: challenge.prizePool || '',
      rules: challenge.rules || {}
    });
    setShowEditModal(true);
  };

  const viewParticipants = async (challengeId) => {
    try {
      const data = await AdminService.getChallengeParticipants(challengeId);
      setParticipants(data.participants || []);
      setShowParticipantsModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load participants');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      league: '10k',
      startingBalance: 10000,
      maxParticipants: '',
      prizePool: '',
      rules: {}
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLeagueLabel = (league) => {
    const labels = { '10k': '$10,000', '100k': '$100,000', '1M': '$1,000,000' };
    return labels[league] || league;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!hasAdminLevel('2')) {
    return (
      <div className="admin-challenges-view">
        <div className="access-denied">
          <FaTrophy size={48} />
          <h2>Admin Level 2+ Required</h2>
          <p>You need to be an admin level 2 or higher to manage challenges.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading challenges...</div>;
  }

  return (
    <div className="admin-challenges-view">
      <div className="challenges-header">
        <h2><FaTrophy /> Challenge Management</h2>
        <button
          className="btn-create"
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
        >
          <FaPlus /> Create Challenge
        </button>
      </div>

      <div className="challenges-list">
        {challenges.length === 0 ? (
          <div className="empty-state">
            <FaTrophy size={48} />
            <p>No challenges yet. Create your first challenge!</p>
          </div>
        ) : (
          challenges.map((challenge) => (
            <div key={challenge.id} className="challenge-card">
              <div className="challenge-header">
                <div className="challenge-title-section">
                  <h3>{challenge.title}</h3>
                  <div className="challenge-badges">
                    {challenge.isPublished && (
                      <span className="badge published">
                        <FaGlobe /> Published
                      </span>
                    )}
                    {challenge.isActive && (
                      <span className="badge active">
                        <FaPlay /> Active
                      </span>
                    )}
                    <span className="badge league">{getLeagueLabel(challenge.league)}</span>
                  </div>
                </div>
                <div className="challenge-stats">
                  <span className="stat">
                    <FaUsers /> {challenge.participantCount || 0} participants
                  </span>
                </div>
              </div>

              {challenge.description && (
                <p className="challenge-description">{challenge.description}</p>
              )}

              <div className="challenge-details">
                <div className="detail">
                  <strong>Start:</strong> {formatDate(challenge.startDate)}
                </div>
                <div className="detail">
                  <strong>End:</strong> {formatDate(challenge.endDate)}
                </div>
                <div className="detail">
                  <strong>Starting Balance:</strong> {formatCurrency(challenge.startingBalance)}
                </div>
                {challenge.maxParticipants && (
                  <div className="detail">
                    <strong>Max Participants:</strong> {challenge.maxParticipants}
                  </div>
                )}
                {challenge.prizePool && (
                  <div className="detail">
                    <strong>Prize Pool:</strong> {formatCurrency(challenge.prizePool)}
                  </div>
                )}
              </div>

              <div className="challenge-actions">
                <button
                  className="btn-action view"
                  onClick={() => viewParticipants(challenge.id)}
                >
                  <FaEye /> View Participants
                </button>
                <button
                  className="btn-action sync"
                  onClick={() => handleAction('sync', challenge.id)}
                >
                  <FaSync /> Sync Values
                </button>
                {!challenge.isPublished ? (
                  <button
                    className="btn-action publish"
                    onClick={() => handleAction('publish', challenge.id)}
                  >
                    <FaGlobe /> Publish
                  </button>
                ) : (
                  <button
                    className="btn-action unpublish"
                    onClick={() => handleAction('unpublish', challenge.id)}
                  >
                    <FaTimes /> Unpublish
                  </button>
                )}
                {!challenge.isActive ? (
                  <button
                    className="btn-action activate"
                    onClick={() => handleAction('activate', challenge.id)}
                  >
                    <FaPlay /> Activate
                  </button>
                ) : (
                  <button
                    className="btn-action deactivate"
                    onClick={() => handleAction('deactivate', challenge.id)}
                  >
                    <FaPause /> Deactivate
                  </button>
                )}
                <button
                  className="btn-action edit"
                  onClick={() => openEditModal(challenge)}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  className="btn-action delete"
                  onClick={() => handleAction('delete', challenge.id)}
                >
                  <FaTrash /> Delete
                </button>
                <button
                  className="btn-action force-delete"
                  onClick={() => handleAction('forceDelete', challenge.id)}
                  title="Force delete (removes challenge even with participants - use only for exploiting)"
                >
                  <FaExclamationTriangle /> Force Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create Challenge</h2>
            <form onSubmit={handleCreateChallenge}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>League *</label>
                  <select
                    value={formData.league}
                    onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                    required
                  >
                    <option value="10k">$10,000</option>
                    <option value="100k">$100,000</option>
                    <option value="1M">$1,000,000</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Starting Balance *</label>
                  <input
                    type="number"
                    value={formData.startingBalance}
                    onChange={(e) => setFormData({ ...formData, startingBalance: e.target.value })}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Max Participants (optional)</label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Prize Pool (optional)</label>
                  <input
                    type="number"
                    value={formData.prizePool}
                    onChange={(e) => setFormData({ ...formData, prizePool: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Challenge</h2>
            <form onSubmit={handleUpdateChallenge}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>League *</label>
                  <select
                    value={formData.league}
                    onChange={(e) => setFormData({ ...formData, league: e.target.value })}
                    required
                  >
                    <option value="10k">$10,000</option>
                    <option value="100k">$100,000</option>
                    <option value="1M">$1,000,000</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Starting Balance *</label>
                  <input
                    type="number"
                    value={formData.startingBalance}
                    onChange={(e) => setFormData({ ...formData, startingBalance: e.target.value })}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Max Participants (optional)</label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Prize Pool (optional)</label>
                  <input
                    type="number"
                    value={formData.prizePool}
                    onChange={(e) => setFormData({ ...formData, prizePool: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="modal-overlay" onClick={() => setShowParticipantsModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h2>Challenge Participants</h2>
            <div className="participants-list">
              {participants.length === 0 ? (
                <p>No participants yet.</p>
              ) : (
                <table className="participants-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>User</th>
                      <th>Portfolio</th>
                      <th>Starting Value</th>
                      <th>Current Value</th>
                      <th>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant, index) => {
                      const change = participant.currentValue - participant.startingValue;
                      const changePercent = participant.startingValue > 0 
                        ? (change / participant.startingValue) * 100 
                        : 0;
                      return (
                        <tr key={participant.id}>
                          <td>{index + 1}</td>
                          <td>{participant.name || participant.email}</td>
                          <td>{participant.portfolioName}</td>
                          <td>{formatCurrency(participant.startingValue)}</td>
                          <td>{formatCurrency(participant.currentValue)}</td>
                          <td className={change >= 0 ? 'positive' : 'negative'}>
                            {change >= 0 ? '+' : ''}{formatCurrency(change)} ({changePercent.toFixed(2)}%)
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowParticipantsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChallengesView;






